import { motion, AnimatePresence } from 'framer-motion'
import {
  ShoppingCart,
  X,
  ArrowRight,
  Trash2,
  Sparkles,
  Plus,
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCart } from '@/contexts/CartContext'
import CartItemCard from './CartItemCard'
import CartCheckoutForm from './CartCheckoutForm'

export default function CartDrawer() {
  const navigate = useNavigate()
  const { t } = useTranslation('cart')
  const {
    isOpen,
    closeCart,
    items,
    checkoutStep,
    setCheckoutStep,
    getTotal,
    clearCart,
    isEmpty,
  } = useCart()

  const handleAddActivity = () => {
    closeCart()
    navigate('/#other-adventures')
  }

  // Close on escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, closeCart])

  const total = getTotal()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50"
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:max-w-md z-50 flex flex-col"
          >
            {/* Main container with glassmorphism */}
            <div className="relative flex-1 flex flex-col bg-gradient-to-br from-gray-900/95 via-gray-900/98 to-black/95 backdrop-blur-xl sm:rounded-l-3xl overflow-hidden border-l border-white/10 shadow-2xl shadow-black/50">
              {/* Animated gradient accent at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-garage-accent via-orange-500 to-garage-primary animate-gradient" />

              {/* Decorative glow effects */}
              <div className="absolute top-20 -right-20 w-40 h-40 bg-garage-accent/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-40 -left-20 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

              {/* Header */}
              <div className="relative shrink-0 bg-black/40 backdrop-blur-sm border-b border-white/10">
                <div className="px-5 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Animated icon container */}
                      <div className="relative">
                        <div className="absolute inset-0 bg-garage-accent/40 rounded-xl blur-lg animate-pulse" />
                        <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-garage-accent to-orange-500 flex items-center justify-center shadow-lg shadow-garage-accent/30">
                          <ShoppingCart className="w-6 h-6 text-white" />
                        </div>
                        {/* Item count badge */}
                        {items.length > 0 && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg"
                          >
                            <span className="text-xs font-bold text-garage-accent">
                              {items.length}
                            </span>
                          </motion.div>
                        )}
                      </div>

                      <div>
                        <h2 className="text-xl font-bold text-white">
                          {checkoutStep === 'cart'
                            ? t('yourCart')
                            : t('checkout')}
                        </h2>
                        <p className="text-white/50 text-sm">
                          {t('selected', { count: items.length })}
                        </p>
                      </div>
                    </div>

                    {/* Close button */}
                    <motion.button
                      onClick={closeCart}
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
                      aria-label={t('closeCart')}
                    >
                      <X className="w-5 h-5 text-white/70" />
                    </motion.button>
                  </div>

                  {/* Step indicator breadcrumb */}
                  {checkoutStep !== 'cart' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 mt-4 px-1"
                    >
                      <button
                        onClick={() => setCheckoutStep('cart')}
                        className="text-sm text-white/50 hover:text-white transition-colors flex items-center gap-1"
                      >
                        <ShoppingCart className="w-3 h-3" />
                        {t('cartStep')}
                      </button>
                      <ArrowRight className="h-3 w-3 text-white/30" />
                      <span className="text-sm text-garage-accent font-medium flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {checkoutStep === 'checkout'
                          ? t('yourDetails')
                          : t('payment')}
                      </span>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-y-auto scrollbar-hide px-5 py-4">
                {checkoutStep === 'cart' ? (
                  // Cart Items View
                  isEmpty() ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col items-center justify-center h-full text-center py-12"
                    >
                      <div className="relative mb-6">
                        <div className="absolute inset-0 bg-white/5 rounded-full blur-xl" />
                        <div className="relative w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                          <ShoppingCart className="w-10 h-10 text-white/30" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {t('empty')}
                      </h3>
                      <p className="text-white/50 mb-8 max-w-xs">
                        {t('emptyBody')}
                      </p>
                      <motion.button
                        onClick={handleAddActivity}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-8 py-4 bg-gradient-to-r from-garage-accent to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-garage-accent/30 transition-all"
                      >
                        {t('browseActivities')}
                      </motion.button>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                    >
                      {items.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <CartItemCard item={item} />
                        </motion.div>
                      ))}

                      {/* Add Activity button */}
                      <motion.button
                        onClick={handleAddActivity}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full flex items-center justify-center gap-2 py-3.5
                                   bg-gradient-to-r from-garage-accent/20 to-orange-500/20
                                   hover:from-garage-accent/30 hover:to-orange-500/30
                                   border border-garage-accent/30 hover:border-garage-accent/50
                                   rounded-xl text-garage-accent font-semibold transition-all"
                      >
                        <Plus className="h-5 w-5" />
                        {t('addAnother')}
                      </motion.button>

                      {/* Clear cart button */}
                      <motion.button
                        onClick={clearCart}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-white/5 hover:bg-red-500/10
                                   border border-white/10 hover:border-red-500/30 rounded-xl text-white/50
                                   hover:text-red-400 transition-all text-sm"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('clearCart')}
                      </motion.button>
                    </motion.div>
                  )
                ) : (
                  // Checkout Form View
                  <CartCheckoutForm />
                )}
              </div>

              {/* Footer with total and CTA */}
              {!isEmpty() && checkoutStep === 'cart' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative shrink-0 bg-black/40 backdrop-blur-sm border-t border-white/10"
                >
                  <div className="px-5 py-5">
                    {/* Total display */}
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <span className="text-white/50 text-sm">
                          {t('totalAmount')}
                        </span>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold bg-gradient-to-r from-garage-accent to-orange-400 bg-clip-text text-transparent">
                            ${total}
                          </span>
                          <span className="text-white/50 text-sm">USD</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-white/30 text-xs">
                          {t('activityCount', { count: items.length })}
                        </span>
                      </div>
                    </div>

                    {/* Checkout button */}
                    <motion.button
                      onClick={() => setCheckoutStep('checkout')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-4 bg-gradient-to-r from-garage-accent to-orange-500
                                 text-white font-bold text-lg rounded-xl
                                 hover:shadow-lg hover:shadow-garage-accent/30 transition-all
                                 flex items-center justify-center gap-3 group"
                    >
                      {t('proceedCheckout')}
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </motion.button>

                    <p className="text-xs text-center text-white/30 mt-4">
                      {t('freeCancellation')}
                    </p>
                  </div>

                  {/* Safe area for mobile */}
                  <div className="h-safe-area-inset-bottom" />
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
